/**
 * @file Submission UI — A2DetailsReadonlySection.
 * A2 on Dataset Overview — same UI as Form A2 (A2Section + summaries).
 */

import React, { useEffect, useMemo, useState } from "react";
import type { FormField, FormSubSection } from "../../interfaces/formInterfaces";
import "../../styles/formBase.css";
import "../../pages/Dashboard/Contributor/Stages/A2/FormA2.css";
import "./LifecycleStageDetailsReadonly.css";
import A2Section from "../../pages/Dashboard/Contributor/Stages/A2/components/A2Section";
import A2EnergySummary from "../../pages/Dashboard/Contributor/Stages/A2/components/A2EnergySummary";
import A2CarbonSummary from "../../pages/Dashboard/Contributor/Stages/A2/components/A2CarbonSummary";
import { useA2Prefill } from "../../pages/Dashboard/Contributor/Stages/A2/hooks/useA2Prefill";
import { buildA2FuelPrefillLayout } from "../../pages/Dashboard/Contributor/Stages/A2/utils/a2FuelLayout";
import { buildA2VehiclePrefillLayout } from "../../pages/Dashboard/Contributor/Stages/A2/utils/a2VehicleLayout";
import type { RawMat } from "../../pages/Dashboard/Contributor/Stages/A2/hooks/useA2Prefill";
import {
  normalizeRawMaterialsFromSubmission,
  type NormalizedLifecycleRawMat,
} from "./lifecycleReadonlyRawMaterials";
import {
  perFinishedCarbonUnitLabel,
  perFinishedEnergyUnitLabel,
  readBackgroundFinishedDeclaredQty,
  resolveBackgroundFinishedMaterialUnitSymbol,
  sumA2EnergyMjForSummary,
} from "../../pages/Dashboard/Contributor/Stages/A1-A3/a1a3SummaryFromSubmission";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import DatasetOverviewReadonlyBlock from "./datasetOverview/DatasetOverviewReadonlyBlock";
import type { AnyObj, ReferenceLabels } from "./readonlyFormDisplay";

const noop = () => {};

const normalizeRawMaterialNameKey = (s: string) =>
  String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

function toA2RawMats(rows: NormalizedLifecycleRawMat[]): RawMat[] {
  return rows.map((r, idx) => ({
    id: r.catalogId ?? r.id,
    catalogId: r.catalogId,
    name: r.name,
    qty: r.qty,
    unitId: r.unitId,
    idx,
  }));
}

/** Type definition — A2Details Readonly Section Props. */
export type A2DetailsReadonlySectionProps = {
  submission: AnyObj;
};

const A2DetailsReadonlySection: React.FC<A2DetailsReadonlySectionProps> = ({
  submission,
}) => {
  const formSchema: AnyObj[] = Array.isArray(submission.formSchema)
    ? submission.formSchema
    : [];

  const stage = formSchema.find((s) => s.code === "RAW_MATERIAL_TRANSPORT_A2");
  const allSections: FormSubSection[] =
    stage && Array.isArray(stage.formSubSections)
      ? (stage.formSubSections as FormSubSection[])
      : [];

  const sectionsVisible = allSections.filter(
    (s) => s.code !== "A2_EMBODIED_ENERGY"
  );

  const normalized = useMemo(
    () => toA2RawMats(normalizeRawMaterialsFromSubmission(submission)),
    [submission]
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [sectionRows, setSectionRows] = useState<Record<string, number>>({});
  const [vehiclesPerRawMaterial, setVehiclesPerRawMaterial] = useState<number[]>(
    []
  );
  const [fuelsPerRawMaterial, setFuelsPerRawMaterial] = useState<number[]>([]);

  const { prefillFromSubmission } = useA2Prefill();

  const embodiedEnergyFieldsForFuel = useMemo(
    () =>
      allSections
        .find((s) => s.code === "A2_EMBODIED_ENERGY")
        ?.fields.filter((f) =>
          ["embodiedEnergy", "embodiedEnergy_unit"].includes(f.name)
        ) ?? [],
    [allSections]
  );

  useEffect(() => {
    if (allSections.length === 0) return;

    const emptyCatalog = new Set<number>();
    const nameToId = new Map<string, number>();

    const vehicleLayout = buildA2VehiclePrefillLayout(
      submission.A2_Vehicle as unknown[] | undefined,
      normalized,
      emptyCatalog,
      nameToId,
      normalizeRawMaterialNameKey
    );
    const fuelLayout = buildA2FuelPrefillLayout(
      submission.A2_Fuel as unknown[] | undefined,
      normalized,
      emptyCatalog,
      nameToId,
      normalizeRawMaterialNameKey
    );

    setVehiclesPerRawMaterial(vehicleLayout.counts);
    setFuelsPerRawMaterial(fuelLayout.counts);
    setValues({});
    setSectionRows({});

    prefillFromSubmission(
      allSections,
      submission,
      normalized,
      setValues,
      setSectionRows,
      null,
      vehicleLayout,
      fuelLayout
    );
  }, [submission, allSections, normalized, prefillFromSubmission]);

  const energyMj = useMemo(
    () => sumA2EnergyMjForSummary(submission),
    [submission]
  );

  const carbonKg = useMemo(() => {
    const s = submission.a2Summary as AnyObj | undefined;
    const n = Number(s?.embodiedCarbonValue ?? s?.embodied_carbon_value);
    return Number.isFinite(n) ? n : 0;
  }, [submission]);

  const finishedQty = useMemo(
    () => readBackgroundFinishedDeclaredQty(submission),
    [submission]
  );

  const finishedMaterialUnitSymbol = useMemo(
    () => resolveBackgroundFinishedMaterialUnitSymbol(submission),
    [submission]
  );

  const perFinishedEnergyUnit = useMemo(
    () => perFinishedEnergyUnitLabel(finishedMaterialUnitSymbol),
    [finishedMaterialUnitSymbol]
  );

  const perFinishedCarbonUnit = useMemo(
    () => perFinishedCarbonUnitLabel(finishedMaterialUnitSymbol),
    [finishedMaterialUnitSymbol]
  );

  const energyPerFinished = useMemo(() => {
    if (finishedQty == null || energyMj <= 0) return null;
    return Math.round((energyMj / finishedQty) * 1000) / 1000;
  }, [energyMj, finishedQty]);

  const carbonPerFinished = useMemo(() => {
    if (finishedQty == null || carbonKg <= 0) return null;
    return Math.round((carbonKg / finishedQty) * 1000) / 1000;
  }, [carbonKg, finishedQty]);

  if (sectionsVisible.length === 0) {
    return (
      <DatasetOverviewFormShell
        variant="lifecycle-a2"
        bare
        emptyMessage="No A2 form sections configured."
      />
    );
  }

  return (
    <DatasetOverviewFormShell variant="lifecycle-a2" bare>
      {sectionsVisible.map((section) => {
        const code = section.code || section.name || String(section.id);
        const rowCount = sectionRows[code] || Math.max(normalized.length, 1);

        return (
          <DatasetOverviewReadonlyBlock
            key={code}
            title={section.name ?? code}
          >
            <A2Section
              section={section}
              rowCount={rowCount}
              rawMaterials={normalized}
              values={values}
              onChange={noop}
              readonly
              vehiclesPerRawMaterial={
                code === "A2_VEHICLE_DETAILS" ? vehiclesPerRawMaterial : undefined
              }
              fuelsPerRawMaterial={
                code === "A2_FUEL_TRANSPORT" ? fuelsPerRawMaterial : undefined
              }
              embodiedEnergyFieldsForFuel={
                code === "A2_FUEL_TRANSPORT"
                  ? embodiedEnergyFieldsForFuel
                  : undefined
              }
              referenceLabels={
                (submission.referenceLabels as ReferenceLabels) ?? undefined
              }
            />
            {code === "A2_FUEL_TRANSPORT" && (
              <A2EnergySummary
                energyMj={energyMj}
                energyPerDeclaredFinishedMj={energyPerFinished}
                perFinishedUnitLabel={perFinishedEnergyUnit}
              />
            )}
            {code === "A2_EMBODIED_CARBON" && (
              <A2CarbonSummary
                totalKgCo2e={carbonKg}
                perFinishedKgCo2e={carbonPerFinished}
                perFinishedUnitLabel={perFinishedCarbonUnit}
              />
            )}
          </DatasetOverviewReadonlyBlock>
        );
      })}
    </DatasetOverviewFormShell>
  );
};

/** React component — A2Details Readonly Section. */
export default A2DetailsReadonlySection;
